import React, { FC, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import {
  destroyMemberMfa,
  inviteMembers,
  passwordReset,
} from '../../../../states/containers/member/memberSlice'
import { format, nowDate } from '../../../../utils/date'
import { useTranslation } from 'react-i18next'
import { sortBy } from 'lodash'
import MemberModal from '../../../components/setting/member/MemberModal'
import ConfirmationModal from '../../../components/setting/member/ConfirmationModal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from '../../../components/common/Tips'
import LoadingSquare from '../../../components/common/LoadingSquare'
import InvitationModal from '../../../components/setting/member/InvitationModal'
import { compile } from '../../../../utils/search'
import ExtraOrderModal from '../../../components/common/ExtraOrderModal'
import CsvModule from '../../../components/setting/CsvModule'
import { URL } from '../../../constants/url'
import { Link, useHistory, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { destroySettingMember, fetchSettingMember, updateSettingMember, updateSettingRole } from '../../../../states/containers/setting/systemSlice'
import { RoleList, RoleTeam, SettingMember } from '../../../../states/types/setting'
import MultiRoleSettingModal from '../../../components/task/MultiRoleSettingModal'
import { fetchAccessToken } from '../../../../states/containers/auth/loginSlice'

interface TargetList {
  member_id: number
  member_name: string
  member_image: string | null
}

interface MiniMenuState {
  display: boolean,
  x: number,
  y: number,
  memberData: SettingMember | undefined,
  status: string,
}

interface InvitationModalState {
  display: boolean,
  targetMemberList: SettingMember[]
}

interface MemberModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  targetMemberData: SettingMember | null
}

interface ConfirmationModalState {
  display: boolean,
  type: string,
  name: string,
  callback: VoidFunction,
}

const DISPLAY_BLOCK_NUMBER = 30

const LABEL_LIST = [
  {
    type: "role",
    label: "権限ロール",
    size: "200px",
  },
  {
    type: "approval_group",
    label: "承認グループ",
    size: "200px",
  },
  {
    type: "display_project_report",
    label: "表示対象プロジェクトレポート数",
    size: "200px",
  },
  {
    type: "display_category_report",
    label: "表示対象カテゴリーレポート数",
    size: "200px",
  },
  {
    type: "project",
    label: "業務仕分け対象プロジェクト数",
    size: "200px",
  },
  {
    type: "mfa_flg",
    label: "多要素認証設定",
    size: "150px",
  },
  {
    type: "connect",
    label: "Google・Microsoft365連携",
    size: "180px",
  },
  {
    type: "slack_connect",
    label: "Slack連携",
    size: "100px",
  },
  {
    type: "email",
    label: "メールアドレス",
    size: "200px",
  },
  {
    type: "app_version",
    label: "バージョン",
    size: "200px",
  },
  {
    type: "last_created_at",
    label: "最終データ収集日時",
    size: "200px",
  },
]

const Index: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()
  const history = useHistory()
  const { language } = useParams<{ language: LangValue }>()

  const FORMAT_DATETIME = t('time.date.formats.with_time')

  const {
    commonPart,
    login,
  } = useSelector((state: RootState) => (state))

  const labelRef = useRef<HTMLDivElement>(null)
  const contentsRef = useRef<HTMLDivElement>(null)
  const barRef = useRef<HTMLDivElement>(null)

  const [isCloseable, setIsCloseable] = useState<boolean>(true)
  const [searchWord, setSearchWord] = useState<string>("")
  const [members, setMembers] = useState<SettingMember[]>()
  const [membersInit, setMembersInit] = useState<SettingMember[]>()
  const [latestClientVersion, setLatestClientVersion] = useState<{windows: string, macos: string}>({ windows: "", macos: "" })
  const [limitMemberNumber, setLimitMemberNumber] = useState<number>(0)
  const [teams, setTeams] = useState<RoleTeam[]>([])
  const [roles, setRoles] = useState<RoleList[]>([])

  const [sortType, setSortType] = useState<string>("")
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const [composition, setComposition] = useState<boolean>(false)

  const [invitationModalState, setInvitationModalState] = useState<InvitationModalState>({
    display: false,
    targetMemberList: [],
  })

  const [memberModalState, setMemberModalState] = useState<MemberModalState>({
    display: false,
    type: "",
    id: undefined,
    targetMemberData: null,
  })

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const [miniMenuState, setMiniMenuState] = useState<MiniMenuState>({
    display: false,
    x: 0,
    y: 0,
    memberData: undefined,
    status: "",
  })

  const [displayExtraOrder, setDisplayExtraOrder] = useState<boolean>(false)

  const [displayBlockNum, setDisplayBlockNum] = useState<number>(1)

  const [scrollBarLeft, setScrollBarLeft] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const [isDisplayMoveMouse, setIsDisplayMoveMouse] = useState(true)
  const [gripNumber, setGripNumber] = useState(0)

  const [sortOrder, setSortOrder] = useState<string>("none")

  const [selectMemberIds, setSelectMemberIds] = useState<number[]>([])
  const [isOpenMultiRoleSettingModal, setIsOpenMultiRoleSettingModal] = useState<boolean>(false)

  const currentScroll = () => Math.max(window.pageYOffset, document.documentElement.scrollTop, document.body.scrollTop)
  const bottomHeight = () => document.getElementsByClassName("main")[0].clientHeight
  const windowHeight = () => document.documentElement.clientHeight

  const onScroll = useCallback(() => {
    const adjustment = window.innerWidth >= 1280 ? 0.90 : 0.85
    if (
      (bottomHeight() * adjustment) < (currentScroll() + windowHeight())
      && (members?.length || 0) >= displayBlockNum * DISPLAY_BLOCK_NUMBER
    ) {
      setDisplayBlockNum((self) => self + 1)
    }
    if (300 < currentScroll()) {
      setIsDisplayMoveMouse(false)
    }
  }, [displayBlockNum, members])

  const unInstallCount = useMemo(() => (
    members?.reduce((s, v) => (!v.client.last_data_received_at ? s + 1 : s), 0)
  ), [members])

  const sort = (key: string) => {
    if (key === "last_created_at") {
      if (sortOrder === "asc") {
        setMembers((cur) => cur?.sort((a, b) => (
          // eslint-disable-next-line max-len
          Number(format(a.client.last_data_received_at || 0, "YYYYMMDDHHmmss")) - Number(format(b.client.last_data_received_at || 0, "YYYYMMDDHHmmss")))
        ))
        setSortOrder("desc")
        return
      }
      if (sortOrder === "desc") {
        setMembers((cur) => cur?.sort((a, b) => a.id - b.id))
        setSortOrder("none")
        return
      }
      setMembers((cur) => cur?.sort((a, b) => (
        Number(format(b.client.last_data_received_at || 0, "YYYYMMDDHHmmss")) - Number(format(a.client.last_data_received_at || 0, "YYYYMMDDHHmmss")))
      ))
      setSortOrder("asc")
    }
  }

  const handleScroll = (sourceRef: React.RefObject<HTMLDivElement>, targetRef: React.RefObject<HTMLDivElement>) => {
    if (sourceRef.current && targetRef.current) {
      targetRef.current.scrollLeft = sourceRef.current.scrollLeft
      const maxScrollLeft = targetRef.current.scrollWidth - targetRef.current.clientWidth
      setScrollBarLeft(((targetRef.current.scrollLeft) / maxScrollLeft) * 100)
      setIsDisplayMoveMouse(false)
    }
  }

  const handleMouseDown = () => {
    setIsDragging(true)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !labelRef.current || !contentsRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const max = rect.width
    const x = Math.round(e.clientX - rect.left)
    const leftPosition = (x / max)
    setScrollBarLeft(leftPosition * 100)
    labelRef.current.scrollLeft = (labelRef.current.scrollWidth - labelRef.current?.clientWidth) * leftPosition
    contentsRef.current.scrollLeft = (contentsRef.current.scrollWidth - contentsRef.current?.clientWidth) * leftPosition
  }

  const handleMouseDownGrip = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.round(e.clientX - rect.left)
    setGripNumber(x + (contentsRef.current?.scrollLeft || 0))
    setIsDragging(true)
  }

  const handleMouseUpGrip = () => {
    setIsDragging(false)
    setGripNumber(0)
  }

  const handleMouseMoveGrip = (e: React.MouseEvent) => {
    if (!isDragging || !barRef.current || !labelRef.current || !contentsRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = Math.round(e.clientX - rect.left)
    labelRef.current.scrollLeft = (gripNumber - x)
    contentsRef.current.scrollLeft = (gripNumber - x)
  }

  const closeMiniMenu = () => {
    setMiniMenuState({
      ...miniMenuState,
      display: false,
    })
  }

  const openMiniMenu = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    const id = Number(event.currentTarget.dataset.id)
    const member = members?.find((v) => v.id === id)
    const status = event.currentTarget.dataset.status as string
    const pageX = event.clientX + (window.scrollX || window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft) - 330
    const pageY = event.clientY + (window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop) - 190
    setMiniMenuState({
      ...miniMenuState,
      display: true,
      x: pageX,
      y: pageY,
      memberData: member,
      status: status,
    })
  }

  const openInvitationModal = () => {
    setInvitationModalState({
      display: true,
      targetMemberList: membersInit?.filter((v) => !v.client.last_data_received_at && v.active) || [],
    })
  }

  const closeInvitationModal = () => {
    setInvitationModalState({
      display: false,
      targetMemberList: [],
    })
  }

  useEffect(() => {
    if (!login.accessToken) return
    if (!commonPart.company?.id) return
    // eslint-disable-next-line no-unused-expressions
    document.querySelector('html')?.classList.add('scroll_contents')
    setIsCloseable(!commonPart.is_first_manager)
  }, [login.accessToken, commonPart.company])

  const closeMemberModal = () => {
    setMemberModalState({
      ...memberModalState,
      targetMemberData: null,
      display: false,
    })
  }

  const addMemberCallback = () => {
    setSearchWord("")
    dispatch(
      fetchSettingMember((result) => {
        setTeams(result.teams)
        setMembersInit(result.members)
        setMembers(result.members)
        setLimitMemberNumber(result.limit_member_number)
        setLatestClientVersion(result.latest_client_version)
        setRoles(result.roles)
      }),
    )
  }

  const modMemberCallback = () => {
    dispatch(fetchAccessToken(
      (data) => {
        if (data.member.role_permission.member_setting) {
          dispatch(
            fetchSettingMember((result) => {
              setTeams(result.teams)
              setMembersInit(result.members)
              setMembers(result.members)
              setLimitMemberNumber(result.limit_member_number)
              setLatestClientVersion(result.latest_client_version)
              setRoles(result.roles)
            }),
          )
        } else {
          history.push(setPath(URL.SETTING_PERSONAL.path, language))
          alert.error(`権限が変更されたため、ユーザー設定ページから移動しました`)
        }
      },
      () => { /**/ },
    ))
  }

  const deleteMember = (memberData: SettingMember) => {
    if (!memberData.id) return
    dispatch(
      destroySettingMember(
        memberData.id,
        () => {
          alert.success(t('setting.member.alert.delete_member', { name: memberData.name }))
          closeMemberModal()
          dispatch(
            fetchSettingMember((result) => {
              setTeams(result.teams)
              setMembersInit(result.members)
              setMembers(result.members)
              setLimitMemberNumber(result.limit_member_number)
              setLatestClientVersion(result.latest_client_version)
              setRoles(result.roles)
            }),
          )
        },
      ),
    )
  }

  const resetMember = (memberData: SettingMember) => {
    if (!memberData.id) return
    dispatch(
      passwordReset(
        memberData.id,
        () => {
          alert.success(t('setting.member.alert.reset_password', { name: memberData.name }))
          closeMemberModal()
          dispatch(
            fetchSettingMember((result) => {
              setTeams(result.teams)
              setMembersInit(result.members)
              setMembers(result.members)
              setLimitMemberNumber(result.limit_member_number)
              setLatestClientVersion(result.latest_client_version)
              setRoles(result.roles)
            }),
          )
        },
      ),
    )
  }

  const disableMemberMfa = (memberData: SettingMember) => {
    if (!memberData.id) return
    dispatch(
      destroyMemberMfa(
        memberData.id,
        () => {
          alert.success(t('setting.member.alert.delete_mfa', { name: memberData.name }))
          closeMemberModal()
          if (!commonPart.company?.id) return
          dispatch(
            fetchSettingMember((result) => {
              setTeams(result.teams)
              setMembersInit(result.members)
              setMembers(result.members)
              setLimitMemberNumber(result.limit_member_number)
              setLatestClientVersion(result.latest_client_version)
              setRoles(result.roles)
            }),
          )
        },
      ),
    )
  }

  const activateMember = (memberData: SettingMember, active: boolean, is_mail?: boolean) => {
    dispatch(
      updateSettingMember(
        memberData.id,
        { active: active },
        () => {
          alert.success(`${memberData.name}さんに${active ? "復帰" : "休眠"}処理をおこないました`)
          if (is_mail) {
            dispatch(
              passwordReset(
                memberData.id,
              ),
            )
          }
          dispatch(
            fetchSettingMember((result) => {
              setTeams(result.teams)
              setMembersInit(result.members)
              setMembers(result.members)
              setLimitMemberNumber(result.limit_member_number)
              setLatestClientVersion(result.latest_client_version)
              setRoles(result.roles)
            }),
          )
        },
        () => {
          alert.error('エラーが発生しました。管理者にお問い合わせください。')
        },
      ),
    )
  }

  // confirmation modal
  const openConfirmationModal = (memberData: SettingMember | null, type: string) => {
    if (!memberData) return
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let callback = (is_mail?: boolean) => {
      // none
    }
    switch (type) {
      case "reset":
        callback = () => resetMember(memberData)
        break
      case "delete":
        callback = () => deleteMember(memberData)
        break
      case "disable":
        callback = () => disableMemberMfa(memberData)
        break
      case "active":
        callback = (is_mail?: boolean) => activateMember(memberData, true, is_mail)
        break
      case "inactive":
        callback = () => activateMember(memberData, false)
        break
      default:
        break
    }
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: memberData.name,
      callback: callback,
    })
    closeMiniMenu()
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const openMemberModal = (memberData: SettingMember | null, type: string) => {
    setMemberModalState({
      ...memberModalState,
      display: true,
      type: type,
      id: memberData?.id || 0,
      targetMemberData: memberData,
    })
    closeMiniMenu()
  }

  const checkLatestVersion = (current: string, platform: string):boolean => {
    let latest
    if (platform === "Windows") {
      latest = latestClientVersion.windows
    } else {
      latest = latestClientVersion.macos
    }
    const current_num = current.split('.').map((value) => (`000${value}`).slice(-3)).join('')
    const latest_num = latest.split('.').map((value) => (`000${value}`).slice(-3)).join('')
    if (Number(current_num) < Number(latest_num)) return false
    return true
  }

  const reset = () => {
    setSearchWord("")
    setSortType("")
    setMembers(membersInit)
    setDisplayBlockNum(1)
  }

  const search = (originalList: SettingMember[] | undefined) => {
    setSortType("")
    if (!searchWord) {
      reset()
      return
    }
    const word = searchWord.split(/\s+/)
    const list = originalList?.filter((member) => {
      if (member === null) return false
      return (compile(word).test(member.name + member.email + member.client.version + member.client.platform + member.kana))
    }) as SettingMember[]
    setMembers(sortBy(list, 'id'))
  }

  const sortMembers = (key: string, type: string) => {
    const tmpList = sortBy(members, key)
    if (type === "desc") {
      return setMembers(tmpList.reverse())
    }
    return setMembers(tmpList)
  }

  const sendInvitation = (memberIds: number[]) => {
    dispatch(
      inviteMembers(
        { member_ids: memberIds },
        () => alert.success(`${memberIds.length}件の招待メールを送信しました。`),
      ),
    )
    closeInvitationModal()
  }

  const keyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !composition) {
      event.currentTarget.blur()
    }
  }

  const reload = () => {
    if (!commonPart.company?.id) return
    setIsLoading(true)
    dispatch(
      fetchSettingMember((result) => {
        setTeams(result.teams)
        setMembersInit(result.members)
        setMembers(result.members)
        setLimitMemberNumber(result.limit_member_number)
        setLatestClientVersion(result.latest_client_version)
        setRoles(result.roles)
        setIsLoading(false)
      }),
    )
  }

  const buildRow = (memberData: SettingMember, type: string) => {
    const labelData = LABEL_LIST.find((v) => v.type === type)
    let displayValue = (<>-</>)
    if (labelData) {
      switch (type) {
        case "role":
          displayValue = (
            <div
              style={{
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: "3",
                overflow: "hidden",
              }}
            >
              {memberData.role_name}
            </div>
          )
          break
        case "email":
          displayValue = (<>{memberData.email}</>)
          break
        case "approval_group":
          displayValue = (
            <>
              {memberData.approval_group.approver.first_group_name}
              {memberData.approval_group.approver.other_count ? ` 他${memberData.approval_group.approver.other_count}件` : ""}
            </>
          )
          break
        case "display_project_report":
          displayValue = (<>{`${memberData.label_group.viewable_count}件`}</>)
          break
        case "display_category_report":
          displayValue = (<>{`${memberData.label_group.viewable_category_count}件`}</>)
          break
        case "project":
          displayValue = (<>{`${memberData.label_group.usable_count}件`}</>)
          break
        case "connect":
          if (memberData.integration.google) {
            displayValue = (
              <>
                <span style={{ color: "#008800" }}>Google連携済み</span>
              </>
            )
          } else if (memberData.integration.microsoft) {
            displayValue = (
              <>
                <span style={{ color: "#008800" }}>Office365連携済み</span>
              </>
            )
          }
          break
        case "slack_connect":
          if (memberData.integration.slack) {
            displayValue = (
              <>
                <span style={{ color: "#008800" }}>Slack連携済み</span>
              </>
            )
          }
          break
        case "app_version":
          if (!memberData.client.platform) break
          displayValue = checkLatestVersion(memberData.client.version || "1", memberData.client.platform)
            ? (<>{`${memberData.client.version} (${memberData.client.platform})`}</>)
            : (
              <Tips description="QaseeClientのバージョンアップをおこなってください。" className="" icon={false}>
                <>
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ color: "#E44242" }} />
                  &nbsp;
                  {`${memberData.client.version} (${memberData.client.platform})`}
                </>
              </Tips>
            )
          break
        case "mfa_flg":
          displayValue = memberData.mfa_enabled
            ? <span style={{ color: "#008800" }}>有効</span>
            : <span style={{ color: "#FF0000" }}>無効</span>
          break
        case "last_created_at":
          displayValue = (
            <>
              {
                memberData.active && memberData.client.login_failed_at && (
                  <span style={{ color: "#FF0000", fontWeight: "bold", textAlign: "center" }}>
                    ログイン失敗
                    <br />
                    {format(memberData.client.login_failed_at, FORMAT_DATETIME)}
                  </span>
                )
              }
              {
                !(memberData.active && memberData.client.login_failed_at) && (
                  <>
                    {
                      memberData.client.last_data_received_at ? (
                        format(memberData.client.last_data_received_at, FORMAT_DATETIME)
                      ) : (
                        <span style={{ color: "#FF0000", fontWeight: "bold" }}>
                          未インストール
                        </span>
                      )
                    }
                  </>
                )
              }
            </>
          )
          break
        default:
          break
      }
    }
    return (
      <div
        key={`member_row_${memberData.id}_${type}`}
        style={{
          width: labelData?.size,
          minWidth: labelData?.size,
          padding: "4px",
          textAlign: "center",
          minHeight: "50px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          userSelect: "none",
          borderRight: type === "last_created_at" ? "none" : "solid 1px #CCCCCC",
        }}
      >
        {displayValue}
      </div>
    )
  }

  const clickMember = (memberId: number) => {
    if (selectMemberIds.includes(memberId)) {
      setSelectMemberIds((cur) => cur.filter((v) => v !== memberId))
    } else {
      setSelectMemberIds((cur) => [...cur, memberId])
    }
  }

  const clickMemberAll = () => {
    if (selectMemberIds.length === members?.length) {
      setSelectMemberIds([])
    } else {
      setSelectMemberIds(members?.map((v) => v.id) || [])
    }
  }

  const buildTargetList = (memberIds: number[]) => {
    const tmp = members?.filter((v) => memberIds.includes(v.id))
    return tmp?.map((v) => (
      {
        member_id: v.id,
        member_name: v.name,
        member_image: v.profile_image,
        role_id: roles.find((w) => w.name === v.role_name)?.id || 0,
      }
    )) || []
  }

  const updateMultiRole = (param: {target_list: TargetList[], role_id: number}, callback: VoidFunction) => {
    const attachedMemberIds = Array.from(new Set(
      [...(roles.find((v) => v.id === param.role_id)?.attached_member_ids || []), ...param.target_list.map((v) => v.member_id)],
    ))
    dispatch(
      updateSettingRole(
        {
          id: param.role_id.toString() || "0",
          attached_member_ids: attachedMemberIds,
        },
        () => {
          callback()
          setSelectMemberIds([])
          alert.success(`対象ユーザーの権限ロールをまとめて変更しました`)
          dispatch(fetchAccessToken(
            (data) => {
              if (data.member.role_permission.member_setting) {
                dispatch(
                  fetchSettingMember((result) => {
                    setTeams(result.teams)
                    setMembersInit(result.members)
                    setMembers(result.members)
                    setLimitMemberNumber(result.limit_member_number)
                    setLatestClientVersion(result.latest_client_version)
                    setRoles(result.roles)
                  }),
                )
              } else {
                history.push(setPath(URL.SETTING_PERSONAL.path, language))
                alert.error(`権限が変更されたため、ユーザー設定ページから移動しました`)
              }
            },
            () => { /**/ },
          ))
        },
      ),
    )
  }

  useEffect(() => {
    if (isLoading) return
    if (!sortType) {
      setMembers(sortBy(members, 'id'))
      return
    }
    const [key, type] = sortType.split("_-_")
    sortMembers(key, type)
  }, [sortType])

  useEffect(() => {
    setIsLoading(true)
    dispatch(
      fetchSettingMember((result) => {
        setTeams(result.teams)
        setMembersInit(result.members)
        setMembers(result.members)
        setLimitMemberNumber(result.limit_member_number)
        setLatestClientVersion(result.latest_client_version)
        setRoles(result.roles)
        setIsLoading(false)
      }),
    )
  }, [])

  useEffect(() => {
    document.addEventListener("scroll", onScroll)
    return () => {
      document.removeEventListener("scroll", onScroll)
    }
  }, [onScroll])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - ユーザー設定`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        ユーザー設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          ユーザー設定
        </h1>
        <div className="tools">
          <div className="button_area">
            {
              (login.feature_config?.change_contract && login.role_permission?.contract_setting) && (
                <button
                  type="button"
                  className="change_contract"
                  onClick={() => setDisplayExtraOrder(true)}
                >
                  <FontAwesomeIcon icon={['far', 'lightbulb']} />
                  ユーザー上限の開放
                </button>
              )
            }
            <button
              type="button"
              className="reload"
              onClick={reload}
              disabled={isLoading}
              style={{
                border: "1px solid #cccccc",
                borderRadius: "5px",
                position: "relative",
                fontSize: "14px",
                padding: "8px 12px",
                color: "#666666",
                margin: "0px",
                backgroundColor: "#ffffff",
              }}
            >
              <Tips
                description="最新の情報に更新"
                className=""
                icon={false}
              >
                <>
                  <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                </>
              </Tips>
            </button>
            {
              limitMemberNumber <= (members?.length || 0) ? (
                <Tips
                  description="ユーザー数が上限に達している為、追加できません"
                  className=""
                  icon={false}
                >
                  <button
                    type="button"
                    className="button add_one_member disabled"
                  >
                    <FontAwesomeIcon icon={['fas', 'plus']} />
                    {t('setting.member.add_member')}
                  </button>
                </Tips>
              ) : (
                <button
                  type="button"
                  className="button add_one_member"
                  onClick={() => openMemberModal(null, "create")}
                >
                  <FontAwesomeIcon icon={['fas', 'plus']} />
                  {t('setting.member.add_member')}
                </button>
              )
            }
          </div>
        </div>
      </section>
      <section className="page_contents content-area">
        <div className="content-block">
          <div
            className="block-header"
          >
            <div />
            {
              !!membersInit?.filter((v) => !v.client.last_data_received_at && v.active).length && (
                <div className="button_area">
                  <button
                    type="button"
                    className="button"
                    onClick={() => openInvitationModal()}
                  >
                    <FontAwesomeIcon icon={['fas', 'paper-plane']} />
                    招待メール一括送信
                  </button>
                </div>
              )
            }
          </div>
          <div
            className="block-header"
            style={{
              backgroundColor: "#F0F1F5",
              padding: "16px 24px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "24px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexFlow: "column",
                  gap: "4px",
                }}
              >
                <div className="member_summary">
                  <div>
                    現在の総数
                    <span className="active_count">
                      {members?.length}
                    </span>
                    名(休眠
                    <span className="inactive_count">
                      {members?.filter((v) => !v.active).length}
                    </span>
                    名を含む)
                  </div>
                  <div className="separator">
                    /
                  </div>
                  <div>
                    上限数
                    <span className="limit_count">
                      {limitMemberNumber}
                    </span>
                    名
                  </div>
                </div>
                {
                  !!unInstallCount && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      QaseeClient 未インストールユーザー数
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#e44242",
                        }}
                      >
                        {unInstallCount}
                      </div>
                      <div>
                        /
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          color: "#666666",
                        }}
                      >
                        {members?.length}
                      </div>
                      名
                    </div>
                  )
                }
              </div>
              <CsvModule
                title={
                  `ユーザー一覧 ${nowDate("YYYYMMDD")}`
                }
                labels={{
                  member_name: "名前",
                  member_kana: "かな",
                  member_code: "コード",
                  mail_address: "メールアドレス",
                  qasee_client_install: "QaseeClientインストール",
                  os: "QaseeClient利用OS",
                  qasee_version: "QaseeClientバージョン",
                  latest_use_date: "最終データ収集日時",
                  mfa: "多要素認証利用",
                  active: "現在の状態",
                }}
                list={
                  members?.map((v) => (
                    {
                      member_name: v.name,
                      member_kana: v.kana,
                      member_code: v.code,
                      mail_address: v.email,
                      qasee_client_install: (v.client.last_data_received_at || v.client.login_failed_at) ? "インストール済み" : "未インストール",
                      os: v.client.platform || "-",
                      qasee_version: v.client.version || "-",
                      latest_use_date: v.client.last_data_received_at || "-",
                      mfa: v.mfa_enabled ? "利用あり" : "利用なし",
                      active: v.active ? "通常" : "休眠",
                    }
                  )) || []
                }
                displayText
              />
            </div>
            <div className="button_area">
              <div className="search_area">
                <input
                  type="text"
                  placeholder={t('member.list.search.placeholder')}
                  onChange={(event) => setSearchWord(event.target.value)}
                  onBlur={() => search(membersInit)}
                  onKeyDown={(event) => keyDown(event)}
                  onCompositionStart={() => setComposition(true)}
                  onCompositionEnd={() => setComposition(false)}
                  value={searchWord}
                />
                {
                  searchWord && (
                    <button type="button" className="reset" onClick={reset}>
                      <FontAwesomeIcon icon={['far', 'times-circle']} />
                    </button>
                  )
                }
              </div>
            </div>
          </div>
          {
            login.role_permission?.role_setting && (
              <div
                style={{
                  border: `solid 1px ${selectMemberIds.length ? "#228b22" : "#999999"}`,
                  borderRadius: "4px",
                  backgroundColor: selectMemberIds.length ? "#f3fff9" : "#EEEEEE",
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "right",
                  gap: "12px",
                  marginBottom: "16px",
                  position: "sticky",
                  top: "60px",
                  zIndex: "2003",
                }}
              >
                {
                  selectMemberIds.length ? (
                    <>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "baseline",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#228b22",
                            fontSize: "20px",
                            fontWeight: "bold",
                          }}
                        >
                          {selectMemberIds.length}
                        </div>
                        <div>
                          名のユーザーが選択されています
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setIsOpenMultiRoleSettingModal(true)}
                        style={{
                          border: "1px solid #228b22",
                          borderRadius: "4px",
                          backgroundColor: "#228b22",
                          color: "#FFFFFF",
                          display: "flex",
                          gap: "4px",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={["fas", "check"]}
                          fixedWidth
                        />
                        まとめて権限ロール設定する
                      </button>
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          marginTop: "8px",
                        }}
                      >
                        ユーザーが選択されていません
                      </div>
                      <button
                        type="button"
                        disabled
                        style={{
                          border: "1px solid #DDDDDD",
                          borderRadius: "4px",
                          backgroundColor: "#DDDDDD",
                          color: "#999999",
                          display: "flex",
                          gap: "4px",
                          cursor: "not-allowed",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={["fas", "check"]}
                          fixedWidth
                        />
                        まとめて権限ロール設定する
                      </button>
                    </>
                  )
                }
              </div>
            )
          }
          {
            isLoading ? (
              <LoadingSquare>
                <>
                  ユーザー情報を取得しています…
                </>
              </LoadingSquare>
            ) : (
              <div className="block-detail">
                <div
                  style={{
                    display: "flex",
                    position: "relative",
                    gap: "1px",
                  }}
                >
                  <div
                    style={{
                      width: "400px",
                      marginTop: "24px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        borderBottom: "solid 2px #CCCCCC",
                        position: "sticky",
                        top: login.role_permission?.role_setting ? "129px" : "80px",
                        backgroundColor: "#FFFFFF",
                        zIndex: "1",
                      }}
                    >
                      {
                        login.role_permission?.role_setting && (
                          <div
                            style={{
                              position: "absolute",
                              top: "calc(50% - 5px)",
                              left: "-20px",
                              transform: "translateY(-50%)",
                              fontSize: "14px",
                              cursor: "pointer",
                              backgroundColor: "#FFFFFF",
                            }}
                            onClick={() => clickMemberAll()}
                            role="none"
                          >
                            <div
                              style={{
                                fontSize: "8px",
                                color: "#999999",
                                transform: "translateX(-25%)",
                                userSelect: "none",
                              }}
                            >
                              全選択
                            </div>
                            <FontAwesomeIcon
                              icon={['far', selectMemberIds.length === members?.length ? 'check-square' : 'square']}
                              style={{
                                backgroundColor: "#ffffff",
                                color: selectMemberIds.length === members?.length ? "#0f9655" : "#666666",
                              }}
                            />
                          </div>
                        )
                      }
                      <div
                        style={{
                          width: "200px",
                          padding: "4px",
                          textAlign: "center",
                          fontWeight: "bold",
                          minHeight: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          userSelect: "none",
                          borderRight: "solid 1px #CCCCCC",
                        }}
                      >
                        名前(社員コード)
                      </div>
                      <div
                        style={{
                          width: "200px",
                          padding: "4px",
                          textAlign: "center",
                          fontWeight: "bold",
                          minHeight: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          userSelect: "none",
                          borderRight: "solid 2px #CCCCCC",
                        }}
                      >
                        チーム(部門コード)
                      </div>
                    </div>
                    {
                      members && members.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((v, i) => (
                        <div
                          style={{
                            position: "relative",
                            display: "flex",
                            borderBottom: "solid 1px #CCCCCC",
                            // eslint-disable-next-line no-nested-ternary
                            backgroundColor: selectMemberIds.includes(v.id) ? "#f3fff9" : i % 2 ? "#fafafa" : "transparent",
                            maxHeight: "50.8px",
                          }}
                        >
                          {
                            login.role_permission?.role_setting && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "-20px",
                                  transform: "translateY(-50%)",
                                  fontSize: "14px",
                                  cursor: "pointer",
                                }}
                                onClick={() => clickMember(v.id)}
                                role="none"
                              >
                                <FontAwesomeIcon
                                  icon={['far', selectMemberIds.includes(v.id) ? 'check-square' : 'square']}
                                  style={{
                                    backgroundColor: "#ffffff",
                                    color: selectMemberIds.includes(v.id) ? "#0f9655" : "#666666",
                                  }}
                                />
                              </div>
                            )
                          }
                          <div
                            style={{
                              position: "relative",
                              width: "200px",
                              padding: "4px 8px",
                              textAlign: "center",
                              minHeight: "50px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "left",
                              userSelect: "none",
                              borderRight: "solid 1px #CCCCCC",
                            }}
                            onClick={() => login.role_permission?.role_setting && clickMember(v.id)}
                            role="none"
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                              }}
                            >
                              <img
                                alt={v.name}
                                src={v.profile_image ?? `/static/images/profile/${(Number(v.id) || 0) % 12}.jpeg`}
                                style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                              />
                              <div
                                style={{
                                  display: "flex",
                                  flexFlow: "column",
                                  justifyContent: "center",
                                  alignItems: "flex-start",
                                }}
                              >
                                <ruby>
                                  {v.name}
                                  <rt>{v.kana}</rt>
                                </ruby>
                                {
                                  v.code && (
                                    <>
                                      {`(${v.code})`}
                                    </>
                                  )
                                }
                              </div>
                              {
                                v.active || (
                                  <Tips
                                    description="休眠中💤"
                                    className=""
                                    icon={false}
                                  >
                                    <>
                                      💤
                                    </>
                                  </Tips>
                                )
                              }
                            </div>
                            <button
                              type="button"
                              style={{
                                position: "absolute",
                                top: "50%",
                                right: "10px",
                                padding: "0",
                                transform: "translateY(-50%)",
                              }}
                              data-id={v.id}
                              onClick={(e) => {
                                e.stopPropagation()
                                openMiniMenu(e)
                              }}
                            >
                              <FontAwesomeIcon icon={['fas', 'ellipsis-h']} />
                            </button>
                          </div>
                          <div
                            style={{
                              width: "200px",
                              padding: "4px",
                              textAlign: "center",
                              minHeight: "50px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              userSelect: "none",
                              borderRight: "solid 2px #CCCCCC",
                            }}
                          >
                            <div>
                              {v.member_teams[0]?.name || "-"}
                              {!!v.member_teams[0]?.code && `(${v.member_teams[0]?.code})`}
                            </div>
                            {
                              v.member_teams?.length > 1 && (
                                <div>
                                  {`他${v.member_teams.length - 1}件`}
                                </div>
                              )
                            }
                          </div>
                        </div>
                      ))
                    }
                  </div>
                  <div
                    style={{
                      width: `calc(100% - 400px)`,
                      marginLeft: "-1px",
                      display: "flex",
                      flexFlow: "column",
                    }}
                  >
                    <div
                      ref={barRef}
                      onMouseMove={handleMouseMove}
                      onMouseDown={handleMouseDown}
                      onMouseLeave={handleMouseUp}
                      onMouseUp={handleMouseUp}
                      role="none"
                      style={{
                        position: "sticky",
                        top: login.role_permission?.role_setting ? "109px" : "60px",
                        zIndex: "2",
                        border: "solid 1px #CCCCCC",
                        borderRadius: "8px",
                        backgroundColor: "#eaf2ff",
                        height: "20px",
                        marginBottom: "4px",
                        padding: "3px 24px",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                          borderRadius: "8px",
                          backgroundColor: "#4472ba",
                          height: "12px",
                          width: "40px",
                          left: `${scrollBarLeft}%` || 0,
                          cursor: isDragging ? "grabbing" : "grab",
                          transform: "translateX(-50%)",
                        }}
                      />
                    </div>
                    <div
                      ref={labelRef}
                      onScroll={() => handleScroll(labelRef, contentsRef)}
                      onMouseMove={handleMouseMoveGrip}
                      onMouseDown={handleMouseDownGrip}
                      onMouseLeave={handleMouseUpGrip}
                      onMouseUp={handleMouseUpGrip}
                      role="none"
                      style={{
                        display: "flex",
                        borderBottom: "solid 2px #CCCCCC",
                        position: "sticky",
                        top: login.role_permission?.role_setting ? "129px" : "80px",
                        backgroundColor: "#FFFFFF",
                        zIndex: "2",
                        overflowX: "scroll",
                        scrollbarWidth: "none",
                        cursor: isDragging ? "grabbing" : "grab",
                      }}
                    >
                      {
                        LABEL_LIST.map((v, i) => (
                          <div
                            style={{
                              minWidth: v.size,
                              padding: "4px",
                              textAlign: "center",
                              fontWeight: "bold",
                              minHeight: "40px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              userSelect: "none",
                              borderRight: i === LABEL_LIST.length - 1 ? "none" : "solid 1px #CCCCCC",
                            }}
                          >
                            {v.label}
                            {
                              v.type === "last_created_at" && (
                                <button
                                  type="button"
                                  onClick={() => sort("last_created_at")}
                                >
                                  {
                                    sortOrder === "none" ? (
                                      <FontAwesomeIcon icon={["fas", "sort"]} fixedWidth />
                                    ) : (
                                      <>
                                        <FontAwesomeIcon
                                          icon={["fas", sortOrder === "asc" ? "sort-amount-down" : "sort-amount-down-alt"]}
                                          fixedWidth
                                        />
                                      </>
                                    )
                                  }
                                </button>
                              )
                            }
                          </div>
                        ))
                      }
                    </div>
                    <div
                      ref={contentsRef}
                      onScroll={() => handleScroll(contentsRef, labelRef)}
                      onMouseMove={handleMouseMoveGrip}
                      onMouseDown={handleMouseDownGrip}
                      onMouseLeave={handleMouseUpGrip}
                      onMouseUp={handleMouseUpGrip}
                      role="none"
                      style={{
                        overflowX: "scroll",
                        scrollbarWidth: "none",
                        position: "relative",
                        cursor: isDragging ? "grabbing" : "grab",
                      }}
                    >
                      {
                        isDisplayMoveMouse && (
                          <div
                            className="fadeout_animation"
                            style={{
                              position: "absolute",
                              top: "100px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              backgroundColor: "rgba(0, 0, 0, 0.5)",
                              borderRadius: "8px",
                              color: "#FFFFFF",
                              width: "300px",
                              height: "200px",
                              zIndex: "1",
                              display: "flex",
                              flexFlow: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "16px",
                            }}
                          >
                            <img src="/static/images/mouse.png" alt="" className="move_horizontal" />
                            横スクロールできます
                          </div>
                        )
                      }
                      <div
                        style={{
                          position: "relative",
                          width: "fit-content",
                        }}
                      >
                        {
                          members && members.slice(0, displayBlockNum * DISPLAY_BLOCK_NUMBER).map((v, i) => (
                            <div
                              key={`member_row_${v.id}`}
                              style={{
                                display: "flex",
                                borderBottom: "solid 1px #CCCCCC",
                                // eslint-disable-next-line no-nested-ternary
                                backgroundColor: selectMemberIds.includes(v.id) ? "#f3fff9" : i % 2 ? "#fafafa" : "transparent",
                                maxHeight: "50.8px",
                              }}
                            >
                              {
                                LABEL_LIST.map((w) => buildRow(v, w.type))
                              }
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          }
        </div>
        <div
          style={{
            top: miniMenuState.y,
            left: miniMenuState.x,
            display: miniMenuState.display ? "block" : "none",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: "0px 0px 5px #CCCCCC",
            border: "1px solid #CCCCCC",
            position: "absolute",
            zIndex: "999999",
            minWidth: "154px",
          }}
        >
          <ul>
            <li>
              <button
                type="button"
                onClick={() => openMemberModal(miniMenuState.memberData || null, "update")}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <FontAwesomeIcon icon={['fas', 'user']} />
                詳細を確認する
              </button>
            </li>
            <li>
              <button
                type="button"
                onClick={() => openConfirmationModal(miniMenuState.memberData || null, "reset")}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                }}
              >
                <FontAwesomeIcon icon={['fas', 'key']} />
                パスワードをリセットする
              </button>
            </li>
            <li>
              {
                miniMenuState.memberData?.active ? (
                  <button
                    type="button"
                    onClick={() => openConfirmationModal(miniMenuState.memberData || null, "inactive")}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <FontAwesomeIcon icon={['far', 'pause-circle']} />
                    休眠する
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => openConfirmationModal(miniMenuState.memberData || null, "active")}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                    }}
                  >
                    <FontAwesomeIcon icon={['far', 'play-circle']} />
                    休眠解除する
                  </button>
                )
              }
            </li>
            {
              miniMenuState.memberData?.active && miniMenuState.memberData?.mfa_enabled && (
                <li>
                  <button
                    type="button"
                    onClick={() => openConfirmationModal(miniMenuState.memberData || null, "disable")}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                      color: "#FF0000",
                      borderTop: "solid 1px #CCCCCC",
                    }}
                  >
                    <FontAwesomeIcon icon={['fas', 'exclamation-circle']} />
                    多要素認証を解除
                  </button>
                </li>
              )
            }
            <li>
              <button
                type="button"
                onClick={() => openConfirmationModal(miniMenuState.memberData || null, "delete")}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "4px",
                  color: "#FF0000",
                  borderTop: "solid 1px #CCCCCC",
                }}
              >
                <FontAwesomeIcon icon={['fas', 'trash-alt']} />
                削除する
              </button>
            </li>
          </ul>
        </div>
        <div className={`close_screen gray ${miniMenuState.display || 'none'}`} role="presentation" onClick={closeMiniMenu} />
        {/* 編集モーダル */}
        <MemberModal
          memberModalState={memberModalState}
          closeCallback={closeMemberModal}
          addCallback={addMemberCallback}
          modCallback={modMemberCallback}
          closeable={isCloseable}
          teamList={teams}
          roleList={roles}
        />
        {/* 確認モーダル */}
        <ConfirmationModal
          confirmationModalState={confirmationModalState}
          closeCallback={closeConfirmationModal}
        />
        {/* 一括招待モーダル */}
        <InvitationModal
          invitationModalState={invitationModalState}
          invitationCallback={(memberIds) => sendInvitation(memberIds)}
          closeCallback={closeInvitationModal}
          closeable
        />
        <ExtraOrderModal
          display={displayExtraOrder}
          closeCallback={() => setDisplayExtraOrder(false)}
        />
        <MultiRoleSettingModal
          targetList={buildTargetList(selectMemberIds) || []}
          roleList={roles || []}
          currentMemberId={login.current_member_id || 0}
          closeCallback={() => setIsOpenMultiRoleSettingModal(false)}
          updateCallback={(param, callback) => updateMultiRole(param, callback)}
          isDisplay={isOpenMultiRoleSettingModal}
        />
      </section>
    </section>
  )
}

export default Index
